/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import cx from 'classnames';
import { useIntl } from 'react-intl';

import { getLogoTitle } from '../../helpers/serviceDeskHelpers';
import { NICE_INCONTACT_BYOSD_TYPE } from '../../util/channelTypes';
import { messages } from './messages';

import './NiceInContactIconMinimal.scss';

interface NiceInContactIconMinimalProps {
  className?: string;
  size?: number;
}

export const NiceInContactIconMinimal = ({
  className,
  size = 32,
  ...others
}: NiceInContactIconMinimalProps) => {
  const { formatMessage: t } = useIntl();
  const intl = useIntl();
  const pxSize = size + 'px';
  const width = 30 + 'px';
  return (
    <div className="nice-container">
      <svg
        aria-label={t(messages.niceInContact)}
        role="img"
        className={cx('NiceInContactIconMinimal', className)}
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={pxSize}
        viewBox="0 0 133.9 52.95"
        {...others}>
        <title>{getLogoTitle(NICE_INCONTACT_BYOSD_TYPE, intl)}</title>
        <g
          aria-label={t(messages.niceInContactLayerTwo)}
          data-name="NiceInContact minimal Layer 2">
          <g
            aria-label={t(messages.niceInContactLayerOne)}
            data-name="NiceInContact minimal Layer 1">
            <polygon points="103.11 0.88 133.9 0.88 133.9 12.83 115.34 12.83 115.34 20.29 130.53 20.29 130.53 32.24 115.34 32.24 115.34 40.14 133.9 40.14 133.9 52.05 103.11 52.05 103.11 0.88" />
            <path d="M86.07,20.61c-.46-3.16-1.43-5.47-2.52-6.51a7.72,7.72,0,0,0-4.26-1.71c-2.36,0-4.25,1.48-5.22,4a27.54,27.54,0,0,0-1.73,10.71c0,9.05,2.35,13.69,7.11,13.69,3.62,0,6-3.16,6.62-8.84H97.94c-.49,6.3-2.22,11.54-5.22,15.35A15.91,15.91,0,0,1,80.09,53,20.1,20.1,0,0,1,69.34,50a18.52,18.52,0,0,1-6.93-9,37.41,37.41,0,0,1-2.54-13.9,40.77,40.77,0,0,1,2.68-15.35,23,23,0,0,1,7.25-9A18.38,18.38,0,0,1,78.66,0c6.63.21,11.52,2.52,14.2,6.09a28.23,28.23,0,0,1,4.75,14.52Z" />
            <rect x="42.91" y="0.88" width="12.21" height="51.12" />
            <polygon points="0 0.88 10.99 0.88 24 29.02 24 17.93 24 0.88 35.77 0.88 35.77 52 24.17 52 11.49 24.33 11.45 36.26 11.49 52 0 52 0 0.88" />
            <rect
              className="cls-1"
              x="97.84"
              y="20.7"
              width="11.22"
              height="11.22"
            />
            <rect
              className="cls-1"
              x="55.12"
              y="20.7"
              width="11.22"
              height="11.22"
            />
            <rect
              className="cls-1"
              x="31.69"
              y="20.7"
              width="11.22"
              height="11.22"
            />
          </g>
        </g>
      </svg>
      <svg
        aria-label={t(messages.niceInContact)}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 176.93 61.21"
        width={width}
        height={pxSize}>
        <g
          aria-label={t(messages.niceInContactLayerFour)}
          data-name="NiceInContact minimal Layer 2 2">
          <g
            aria-label={t(messages.niceInContactLayerThree)}
            data-name="Layer 1">
            <path d="M45.25,40.79a.3.3,0,0,0-.21-.1H38.41a.29.29,0,0,0-.29.27C37.53,49.54,32,55.09,23.94,55.09,13.46,55.09,7.2,47.4,7.2,34.53c0-12.32,6.35-20,16.59-20,7.35,0,12.41,3.8,13.89,10.43a.28.28,0,0,0,.27.22h6.63a.3.3,0,0,0,.22-.1.26.26,0,0,0,.06-.23C43.37,14.74,35.35,8.44,23.94,8.44,16.62,8.44,10.55,11,6.4,15.89,2.27,20.58,0,27.36,0,35,0,43,2,49.28,6.25,54.06s9.9,7.15,17.09,7.15C36,61.21,44,53.84,45.32,41A.27.27,0,0,0,45.25,40.79Z" />
            <path d="M86.94,59.69,69.68,34.51,85.87,10.56a.56.56,0,0,0-.45-.91H78a.56.56,0,0,0-.47.25L65.08,28.68l-12-18.77a.59.59,0,0,0-.48-.26h-8a.56.56,0,0,0-.5.3.57.57,0,0,0,0,.58l16.21,24L43.06,59.69a.57.57,0,0,0,.47.89h7.53a.55.55,0,0,0,.47-.25L64.92,40.49,77.86,60.32a.56.56,0,0,0,.48.26h8.13a.57.57,0,0,0,.47-.89Z" />
            <path
              className="cls-1"
              d="M110.73,40c0-8.63-5.23-14.43-13-14.43s-13,5.94-13,14.43,5.36,14.43,13,14.43S110.73,48.67,110.73,40Zm-3.57,0c0,6.57-3.88,11.15-9.45,11.15-6.49,0-9.45-5.78-9.45-11.15,0-6.57,3.89-11.15,9.45-11.15S107.16,33.47,107.16,40Z"
            />
            <path
              className="cls-1"
              d="M117.57,26.64l-.34-.33-.2-.09h-2.32l-.2.09-.34.33-.09.2v26.4l.09.2.34.33.2.09H117l.2-.09.34-.33.08-.2V39c0-6.16,3.45-10.14,8.78-10.14,4.61,0,6.76,2.37,6.76,7.45v16.9l.08.2.34.33.2.09h2.32l.2-.09.34-.33.08-.2V36.55c0-3.27-.62-5.64-2-7.48-1.75-2.32-4.47-3.46-8.3-3.46a11.24,11.24,0,0,0-8.83,4.07V26.84Z"
            />
            <path
              className="cls-1"
              d="M163.86,41l.34-.34.08-.2v-1c0-4.53-1.21-7.88-3.81-10.54A12,12,0,0,0,152,25.61,11.58,11.58,0,0,0,143.34,29a15.87,15.87,0,0,0-4.11,11.25c0,4,.84,7,2.74,9.59a11.74,11.74,0,0,0,9.81,4.64c6.78,0,11.15-3.59,12.28-10.09l0-.21-.07-.23-.33-.4-.22-.1h-2.32l-.18.07-.33.27-.1.16,0,.15a8.4,8.4,0,0,1-8.74,7.1A8.1,8.1,0,0,1,145,47.87a12,12,0,0,1-2.16-6.78h20.85Zm-21-3.19a12.28,12.28,0,0,1,2.8-6.19,8.43,8.43,0,0,1,12.2-.25,10.22,10.22,0,0,1,2.78,6.44Z"
            />
            <path
              className="cls-1"
              d="M176.93,39.22a21.27,21.27,0,0,0-18.28-21A21.23,21.23,0,0,0,127,6.44,17.1,17.1,0,0,0,113.56,0a17.41,17.41,0,0,0-16.5,12.15A17.33,17.33,0,0,0,82.27,26.68a2,2,0,0,0,1.66,2.23,2,2,0,0,0,2.23-1.65A13.38,13.38,0,0,1,98.7,15.94a2,2,0,0,0,1.83-1.54,13.42,13.42,0,0,1,13-10.47,13.28,13.28,0,0,1,11.32,6.28,2,2,0,0,0,1.32.89,2,2,0,0,0,1.55-.38,17.28,17.28,0,0,1,27.43,9.74A2,2,0,0,0,157,22a17.29,17.29,0,0,1-1.25,34.53H91.34a1.71,1.71,0,0,0-.49.07l-.2.17-.19.52,0,.26,1.81,2.64.38.24.13,0h62.9A21.24,21.24,0,0,0,176.93,39.22Z"
            />
          </g>
        </g>
      </svg>
    </div>
  );
};
